import { useEffect, useState } from 'react'
import { Loading } from '../../../components/Loading/Loading'
import { StatusCard } from './components/StatusCard/StatusCard'
import { ChartWrapper } from './components/ChartWrapper/ChartWrapper'
import { DynamicTable, TableColumn } from './components/DynamicTable/DynamicTable'
import { StorageStatusChart } from './components/Charts/StorageStatusChart'
import { PublicityStatusChart } from './components/Charts/PublicityStatusChart'
import { PostsStatusChart } from './components/Charts/PostsStatusChart'
import { PostsExpiringChart } from './components/Charts/PostsExpiringChart'
import { StoresByClassificationChart } from './components/Charts/StoresByClassificationChart'
import { PublicityByTypeChart } from './components/Charts/PublicityByTypeChart'
import { PublicityByStoreChart } from './components/Charts/PublicityByStoreChart'
import { PublicityTimelineChart } from './components/Charts/PublicityTimelineChart'
import { ExpiringPublicityChart } from './components/Charts/ExpiringPublicityChart'
import { useDashboard } from './factory/useDashboard'
import { useDashboardStats } from './factory/useDashboardStats'

export function DashBoardAgregador() {
    const [activeTab, setActiveTab] = useState<'lojas' | 'publicidades' | 'categorias' | 'paginas' | 'posts'>('lojas')

    const {
        getAggregatorDinamicPage,
        getLojasAgregador,
        getAggregatorClassification,
        getAggregatorPublicity,
        getAggregatorPosts,
        posts,
        aggregatorCategories,
        aggregatorPublicity,
        tableLoading,
        lojasAgregador,
        aggregatorDinamicPage,
    } = useDashboard()

    const {
        stats,
        getStoreStatusDistribution,
        getPublicityStatusDistribution,
        getPostStatusDistribution,
        getStoresByClassification,
        getPublicityByType,
        getPublicityByStore,
        getPublicityTimeline,
        getExpiringPublicity,
        getExpiringPosts,
        storesStats,
        publicityStats,
        classificationsStats,
        pagesStats,
        postsStats,
    } = useDashboardStats(
        lojasAgregador,
        aggregatorPublicity,
        aggregatorCategories,
        aggregatorDinamicPage,
        posts
    )

    // Load data on mount
    useEffect(() => {
        getLojasAgregador()
        getAggregatorClassification()
        getAggregatorPublicity()
        getAggregatorPosts()
        getAggregatorDinamicPage()
    }, [])

    if (tableLoading) {
        return <Loading />
    }

    return (
        <div className="container-fluid">
            {/* Header Section */}
            <div className="row mb-4">
                <div className="col-12">
                    <h1 className="mb-1 fw-bold">Dashboard Agregador</h1>
                    <p className="text-muted mb-0">
                        Visualize estatísticas gerais do seu agregador
                    </p>
                </div>
            </div>

            {/* Key Metrics Row */}
            <div className="row mb-4 g-3">
                <div className="col-sm-6 col-md-3">
                    <StatusCard
                        title="Lojas Ativas"
                        value={storesStats.activeStoresCount}
                        bgColor="bg-light-primary"
                        textColor="text-primary"
                    />
                </div>
                <div className="col-sm-6 col-md-3">
                    <StatusCard
                        title="Publicidades Ativas"
                        value={publicityStats.activePublicityCount}
                        bgColor="bg-light-success"
                        textColor="text-success"
                    />
                </div>
                <div className="col-sm-6 col-md-3">
                    <StatusCard
                        title="Publicidades Expiradas"
                        value={publicityStats.expiredPublicityCount}
                        bgColor="bg-light-danger"
                        textColor="text-danger"
                    />
                </div>
                <div className="col-sm-6 col-md-3">
                    <StatusCard
                        title="Publicidades a Vencer"
                        value={publicityStats.expiringPublicityCount}
                        bgColor="bg-light-warning"
                        textColor="text-warning"
                    />
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="card border-0 shadow-sm">
                <ul
                    className="nav nav-tabs nav-line-tabs nav-line-tabs-2x border-0 fs-6 fw-semibold p-4"
                    role="tablist"
                >
                    <li className="nav-item" role="presentation">
                        <a
                            className={`nav-link ${activeTab === 'lojas' ? 'active' : ''}`}
                            href="#lojas"
                            data-bs-toggle="tab"
                            role="tab"
                            onClick={() => setActiveTab('lojas')}
                        >
                            Lojas
                        </a>
                    </li>
                    <li className="nav-item" role="presentation">
                        <a
                            className={`nav-link ${activeTab === 'publicidades' ? 'active' : ''}`}
                            href="#publicidades"
                            data-bs-toggle="tab"
                            role="tab"
                            onClick={() => setActiveTab('publicidades')}
                        >
                            Publicidades
                        </a>
                    </li>
                    <li className="nav-item" role="presentation">
                        <a
                            className={`nav-link ${activeTab === 'categorias' ? 'active' : ''}`}
                            href="#categorias"
                            data-bs-toggle="tab"
                            role="tab"
                            onClick={() => setActiveTab('categorias')}
                        >
                            Categorias
                        </a>
                    </li>
                    <li className="nav-item" role="presentation">
                        <a
                            className={`nav-link ${activeTab === 'paginas' ? 'active' : ''}`}
                            href="#paginas"
                            data-bs-toggle="tab"
                            role="tab"
                            onClick={() => setActiveTab('paginas')}
                        >
                            Páginas Dinâmicas
                        </a>
                    </li>
                    <li className="nav-item" role="presentation">
                        <a
                            className={`nav-link ${activeTab === 'posts' ? 'active' : ''}`}
                            href="#posts"
                            data-bs-toggle="tab"
                            role="tab"
                            onClick={() => setActiveTab('posts')}
                        >
                            Posts
                        </a>
                    </li>
                </ul>

                {/* Tab Content */}
                <div className="tab-content">
                    {/* Aba Lojas */}
                    <div
                        className={`tab-pane fade ${activeTab === 'lojas' ? 'show active' : ''}`}
                        id="lojas"
                        role="tabpanel"
                    >
                        <div className="p-4">
                            {/* Lojas - Metrics Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Total de Lojas"
                                        value={storesStats.totalStoresCount}
                                        bgColor="bg-light-info"
                                        textColor="text-info"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Lojas Ativas"
                                        value={storesStats.activeStoresCount}
                                        bgColor="bg-light-success"
                                        textColor="text-success"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Lojas Inativas"
                                        value={storesStats.inactiveStoresCount}
                                        bgColor="bg-light-danger"
                                        textColor="text-danger"
                                    />
                                </div>
                            </div>

                            {/* Lojas - Charts Row 1 */}
                            <div className="row mb-4 g-3">
                                <div className="col-lg-6">
                                    <ChartWrapper title="Status das Lojas">
                                        <StorageStatusChart data={getStoreStatusDistribution} />
                                    </ChartWrapper>
                                </div>
                                <div className="col-lg-6">
                                    <ChartWrapper title="Lojas por Categoria">
                                        <StoresByClassificationChart
                                            data={getStoresByClassification}
                                        />
                                    </ChartWrapper>
                                </div>
                            </div>

                            {/* Lojas - Table */}
                            <DynamicTable
                                title="Lojas Cadastradas"
                                columns={[
                                    { key: 'name', label: 'Nome' },
                                    { key: 'cnpj', label: 'CNPJ' },
                                    {
                                        key: 'active',
                                        label: 'Status',
                                        render: (value) => (
                                            <span
                                                className={`badge ${value
                                                    ? 'bg-light-success text-success'
                                                    : 'bg-light-danger text-danger'
                                                }`}
                                            >
                                                {value ? 'Ativa' : 'Inativa'}
                                            </span>
                                        ),
                                    },
                                    { key: 'type', label: 'Tipo', render: (value) => value || 'N/A' },
                                ]}
                                data={lojasAgregador}
                                maxRows={5}
                            />
                        </div>
                    </div>

                    {/* Aba Publicidades */}
                    <div
                        className={`tab-pane fade ${activeTab === 'publicidades' ? 'show active' : ''}`}
                        id="publicidades"
                        role="tabpanel"
                    >
                        <div className="p-4">
                            {/* Publicidades - Metrics Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-sm-6 col-md-2">
                                    <StatusCard
                                        title="Total"
                                        value={publicityStats.totalPublicityCount}
                                        bgColor="bg-light-info"
                                        textColor="text-info"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-2">
                                    <StatusCard
                                        title="Ativas"
                                        value={publicityStats.activePublicityCount}
                                        bgColor="bg-light-success"
                                        textColor="text-success"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-2">
                                    <StatusCard
                                        title="Inativas"
                                        value={publicityStats.inactivePublicityCount}
                                        bgColor="bg-light-secondary"
                                        textColor="text-secondary"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-2">
                                    <StatusCard
                                        title="Expiradas"
                                        value={publicityStats.expiredPublicityCount}
                                        bgColor="bg-light-danger"
                                        textColor="text-danger"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-4">
                                    <StatusCard
                                        title="A Vencer (30 dias)"
                                        value={publicityStats.expiringPublicityCount}
                                        bgColor="bg-light-warning"
                                        textColor="text-warning"
                                    />
                                </div>
                            </div>

                            {/* Publicidades - Charts Row 1 */}
                            <div className="row mb-4 g-3">
                                <div className="col-lg-6">
                                    <ChartWrapper title="Status das Publicidades">
                                        <PublicityStatusChart
                                            data={getPublicityStatusDistribution}
                                        />
                                    </ChartWrapper>
                                </div>
                                <div className="col-lg-6">
                                    <ChartWrapper title="Publicidades por Tipo">
                                        <PublicityByTypeChart data={getPublicityByType} />
                                    </ChartWrapper>
                                </div>
                            </div>

                            {/* Publicidades - Charts Row 2 */}
                            <div className="row mb-4 g-3">
                                <div className="col-12">
                                    <ChartWrapper title="Publicidades por Loja (Top 10)">
                                        <PublicityByStoreChart data={getPublicityByStore} />
                                    </ChartWrapper>
                                </div>
                            </div>

                            {/* Publicidades - Charts Row 3 */}
                            {getExpiringPublicity.length > 0 && (
                                <div className="row mb-4 g-3">
                                    <div className="col-12">
                                        <ChartWrapper title="Publicidades a Vencer">
                                            <ExpiringPublicityChart
                                                data={getExpiringPublicity}
                                                height={300}
                                            />
                                        </ChartWrapper>
                                    </div>
                                </div>
                            )}

                            {/* Publicidades - Timeline Chart */}
                            <div className="row">
                                <div className="col-12">
                                    <ChartWrapper title="Publicidades Criadas (Últimos 12 meses)">
                                        <PublicityTimelineChart data={getPublicityTimeline} />
                                    </ChartWrapper>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Aba Categorias */}
                    <div
                        className={`tab-pane fade ${activeTab === 'categorias' ? 'show active' : ''}`}
                        id="categorias"
                        role="tabpanel"
                    >
                        <div className="p-4">
                            {/* Categorias - Metrics Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Total de Categorias"
                                        value={classificationsStats.totalClassificationsCount}
                                        bgColor="bg-light-info"
                                        textColor="text-info"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Categorias Ativas"
                                        value={classificationsStats.activeClassificationsCount}
                                        bgColor="bg-light-success"
                                        textColor="text-success"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Categorias Inativas"
                                        value={classificationsStats.inactiveClassificationsCount}
                                        bgColor="bg-light-danger"
                                        textColor="text-danger"
                                    />
                                </div>
                            </div>

                            {/* Categorias - Charts Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-12">
                                    <ChartWrapper title="Lojas por Categoria">
                                        <StoresByClassificationChart
                                            data={getStoresByClassification}
                                        />
                                    </ChartWrapper>
                                </div>
                            </div>

                            {/* Categorias - Table */}
                            <DynamicTable
                                title="Lista de Categorias"
                                columns={[
                                    { key: 'name', label: 'Nome' },
                                    { key: 'type', label: 'Tipo' },
                                    {
                                        key: 'status',
                                        label: 'Status',
                                        render: (value) => (
                                            <span
                                                className={`badge ${value
                                                    ? 'bg-light-success text-success'
                                                    : 'bg-light-danger text-danger'
                                                }`}
                                            >
                                                {value ? 'Ativa' : 'Inativa'}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'id',
                                        label: 'Lojas Atreladas',
                                        render: (_, row) => {
                                            const storesCount = lojasAgregador.filter(
                                                (s) => s.classificationId === row.id.toString()
                                            ).length
                                            return storesCount
                                        },
                                    },
                                ]}
                                data={aggregatorCategories}
                                maxRows={10}
                            />
                        </div>
                    </div>

                    {/* Aba Páginas Dinâmicas */}
                    <div
                        className={`tab-pane fade ${activeTab === 'paginas' ? 'show active' : ''}`}
                        id="paginas"
                        role="tabpanel"
                    >
                        <div className="p-4">
                            {/* Páginas - Metrics Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Total de Páginas"
                                        value={pagesStats.totalPagesCount}
                                        bgColor="bg-light-info"
                                        textColor="text-info"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Páginas Ativas"
                                        value={pagesStats.activePagesCount}
                                        bgColor="bg-light-success"
                                        textColor="text-success"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Páginas Inativas"
                                        value={pagesStats.inactivePagesCount}
                                        bgColor="bg-light-danger"
                                        textColor="text-danger"
                                    />
                                </div>
                            </div>

                            {/* Páginas - Table */}
                            <DynamicTable
                                title="Páginas Dinâmicas"
                                columns={[
                                    { key: 'title', label: 'Título' },
                                    { key: 'slug', label: 'Slug' },
                                    {
                                        key: 'status',
                                        label: 'Status',
                                        render: (value) => (
                                            <span
                                                className={`badge ${value
                                                    ? 'bg-light-success text-success'
                                                    : 'bg-light-danger text-danger'
                                                }`}
                                            >
                                                {value ? 'Ativa' : 'Inativa'}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'grids',
                                        label: 'Conteúdo',
                                        render: (value) => `${value?.length || 0} grids`,
                                    },
                                    {
                                        key: 'created',
                                        label: 'Data Criação',
                                        render: (value) =>
                                            new Date(value).toLocaleDateString('pt-BR'),
                                    },
                                ]}
                                data={aggregatorDinamicPage}
                                maxRows={10}
                            />
                        </div>
                    </div>

                    {/* Aba Posts */}
                    <div
                        className={`tab-pane fade ${activeTab === 'posts' ? 'show active' : ''}`}
                        id="posts"
                        role="tabpanel"
                    >
                        <div className="p-4">
                            {/* Posts - Metrics Row */}
                            <div className="row mb-4 g-3">
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Total de Posts"
                                        value={postsStats.totalPostsCount}
                                        bgColor="bg-light-info"
                                        textColor="text-info"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Posts Ativos"
                                        value={postsStats.activePostsCount}
                                        bgColor="bg-light-success"
                                        textColor="text-success"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Posts Inativos"
                                        value={postsStats.inactivePostsCount}
                                        bgColor="bg-light-danger"
                                        textColor="text-danger"
                                    />
                                </div>
                                <div className="col-sm-6 col-md-3">
                                    <StatusCard
                                        title="Posts a Vencer (30 dias)"
                                        value={postsStats.expiringPostsCount}
                                        bgColor="bg-light-warning"
                                        textColor="text-warning"
                                    />
                                </div>
                            </div>

                            {/* Posts - Charts Row 1 */}
                            <div className="row mb-4 g-3">
                                <div className="col-lg-6">
                                    <ChartWrapper title="Status dos Posts">
                                        <PostsStatusChart data={getPostStatusDistribution} />
                                    </ChartWrapper>
                                </div>
                            </div>

                            {/* Posts - Charts Row 2 (Expiring) */}
                            {getExpiringPosts.length > 0 && (
                                <div className="row mb-4 g-3">
                                    <div className="col-12">
                                        <ChartWrapper title="Posts a Vencer">
                                            <PostsExpiringChart
                                                data={getExpiringPosts}
                                                height={300}
                                            />
                                        </ChartWrapper>
                                    </div>
                                </div>
                            )}

                            {/* Posts - Table */}
                            <DynamicTable
                                title="Posts Cadastrados"
                                columns={[
                                    {
                                        key: 'id',
                                        label: 'ID',
                                        render: (value) => value || 'N/A',
                                    },
                                    {
                                        key: 'title',
                                        label: 'Título',
                                        render: (value) => value || 'Sem título',
                                    },
                                    {
                                        key: 'status',
                                        label: 'Status',
                                        render: (value) => (
                                            <span
                                                className={`badge ${value
                                                    ? 'bg-light-success text-success'
                                                    : 'bg-light-danger text-danger'
                                                }`}
                                            >
                                                {value ? 'Ativo' : 'Inativo'}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: 'dateFin',
                                        label: 'Data de Término',
                                        render: (value) =>
                                            value ? new Date(value).toLocaleDateString('pt-BR') : 'N/A',
                                    },
                                ]}
                                data={posts}
                                emptyMessage="Nenhum post disponível"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}